import React, { useState, useEffect } from 'react';
import { X, GripVertical, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const topicCategories = [
  {
    name: 'Science',
    icon: '🧪',
    color: 'bg-pink-500',
    topics: ['Science', 'Space', 'Wildlife', 'Environment', 'Neuroscience', 'Physics'],
  },
  {
    name: 'Education',
    icon: '🎓',
    color: 'bg-orange-500',
    topics: ['Education', 'Jobs', 'Online education', 'Higher education'],
  },
  {
    name: 'Lifestyle',
    icon: '✈️',
    color: 'bg-red-500',
    topics: ['Vehicles', 'Arts and design', 'Beauty', 'Food', 'Travel', 'Fashion'],
  },
  {
    name: 'Business',
    icon: '💼',
    color: 'bg-blue-500',
    topics: ['Economy', 'Finance', 'Markets', 'Startups', 'Real estate'],
  },
  {
    name: 'Technology',
    icon: '💻',
    color: 'bg-purple-500',
    topics: ['AI', 'Gadgets', 'Apps', 'Software', 'Cybersecurity', 'Gaming'],
  },
  {
    name: 'Sports',
    icon: '⚽',
    color: 'bg-green-500',
    topics: ['Football', 'Soccer', 'Basketball', 'Baseball', 'Tennis', 'Golf'],
  },
  {
    name: 'Entertainment',
    icon: '🎬',
    color: 'bg-yellow-500',
    topics: ['Movies', 'Music', 'TV', 'Celebrities', 'Books'],
  },
  {
    name: 'World',
    icon: '🌍',
    color: 'bg-teal-500',
    topics: ['US', 'World', 'Politics', 'Europe', 'Asia', 'Africa'],
  },
];

const CustomizeModal = ({ isOpen, onClose, onSave, initialTopics = [] }) => {
  const [selectedTopics, setSelectedTopics] = useState(new Set(initialTopics));
  const [orderedTopics, setOrderedTopics] = useState(initialTopics);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSelected, setShowSelected] = useState(false);

  // Use a key-based reset approach instead of useEffect with setState
  const resetKey = isOpen ? 'open' : 'closed';
  
  // Initialize state based on props when needed
  const effectiveSelectedTopics = selectedTopics.size === 0 && isOpen 
    ? new Set(initialTopics) 
    : selectedTopics;

  const toggleTopic = (topic) => {
    const newSelected = new Set(effectiveSelectedTopics);
    if (newSelected.has(topic)) {
      newSelected.delete(topic);
      setOrderedTopics(orderedTopics.filter(t => t !== topic));
    } else {
      if (newSelected.size < 12) {
        newSelected.add(topic);
        if (!orderedTopics.includes(topic)) {
          setOrderedTopics([...orderedTopics, topic]);
        }
      }
    }
    setSelectedTopics(newSelected);
    setHasChanges(true);
  };

  const removeTopic = (topic) => {
    const newSelected = new Set(selectedTopics);
    newSelected.delete(topic);
    setSelectedTopics(newSelected);
    setOrderedTopics(orderedTopics.filter(t => t !== topic));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(orderedTopics);
    onClose();
  };

  const handleReset = () => {
    const defaultTopics = ['US', 'World', 'Technology', 'Business', 'Sports', 'Entertainment'];
    setSelectedTopics(new Set(defaultTopics));
    setOrderedTopics(defaultTopics);
    setHasChanges(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden shadow-2xl sm:mx-4">
        {/* Header - Compact on mobile */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-lg sm:text-2xl font-normal text-gray-900 dark:text-white">
              Customize topics
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Choose up to 12 topics
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
          </button>
        </div>

        {/* Selected Topics Bar - Mobile only */}
        <div className="sm:hidden px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-100 dark:border-emerald-800">
          <button 
            onClick={() => setShowSelected(!showSelected)}
            className="w-full flex items-center justify-between"
          >
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
              Selected ({orderedTopics.length}/12)
            </span>
            {showSelected ? (
              <ChevronUp className="h-4 w-4 text-emerald-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-emerald-600" />
            )}
          </button>
          
          {showSelected && orderedTopics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {orderedTopics.map((topic) => (
                <span
                  key={topic}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 rounded-full text-xs"
                >
                  {topic}
                  <button onClick={() => removeTopic(topic)}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col sm:flex-row sm:divide-x divide-gray-200 dark:divide-gray-800 max-h-[50vh] sm:max-h-[55vh]">
          {/* Topic Selection - Grid on mobile */}
          <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
            {topicCategories.map((category, idx) => (
              <div key={idx} className="mb-4 sm:mb-6">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full ${category.color} flex items-center justify-center text-[10px] sm:text-xs`}>
                    {category.icon}
                  </div>
                  <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                    {category.name}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {category.topics.map((topic) => (
                    <button
                      key={topic}
                      onClick={() => toggleTopic(topic)}
                      disabled={!selectedTopics.has(topic) && selectedTopics.size >= 12}
                      className={`flex items-center gap-1 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm transition-all ${
                        selectedTopics.has(topic)
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-2 border-emerald-500'
                          : selectedTopics.size >= 12
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 border-2 border-transparent cursor-not-allowed'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:border-gray-300'
                      }`}
                    >
                      {topic}
                      {selectedTopics.has(topic) && (
                        <Check className="h-3 w-3 ml-0.5" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Re-order Panel - Hidden on mobile (shown in expandable bar above) */}
          <div className="hidden sm:block w-72 p-6 overflow-y-auto bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
              Your topics ({orderedTopics.length}/12)
            </h3>
            {orderedTopics.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select topics from the left.
              </p>
            ) : (
              <div className="space-y-2">
                {orderedTopics.map((topic) => (
                  <div
                    key={topic}
                    className="flex items-center justify-between px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-sm text-gray-900 dark:text-white border border-emerald-200 dark:border-emerald-800"
                  >
                    <span>{topic}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => removeTopic(topic)}
                        className="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-800 rounded"
                      >
                        <X className="h-3 w-3 text-gray-500" />
                      </button>
                      <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Compact on mobile */}
        <div className="flex items-center justify-between p-3 sm:p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30">
          <button 
            onClick={handleReset}
            className="text-xs sm:text-sm text-emerald-500 hover:underline"
          >
            Reset
          </button>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-3 sm:px-6 text-xs sm:text-sm h-8 sm:h-10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || orderedTopics.length === 0}
              className={`px-3 sm:px-6 text-xs sm:text-sm h-8 sm:h-10 ${
                hasChanges && orderedTopics.length > 0
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizeModal;
